import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import { useAuth0 } from "@auth0/auth0-react"
import {
  Container, Row, Col, Card, CardBody, CardTitle, Badge, Input, Label, Button, Alert, Spinner,
  ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from "reactstrap"
import Select from "react-select"
import { withTranslation } from "react-i18next"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

import BreadcrumbLink from "../../components/Common/BreadcrumbLink"
import { Permissions } from "../../constants/enums"
import {
  getServiceValidationKanban, getServiceValidationList, getOrgsAdmin,
  getServiceValidationExport, postServiceValidationHold,
  postServiceValidationRelease,
} from "../../helpers/api_rombo_helper"
import { listPlaces } from "../../common/data"
import { fmtMxDate, fmtMxTime } from "../../helpers/datetime"
import ServiceValidationViewToggle from "./ServiceValidationViewToggle"
import { KanbanSkeleton, TRIP_STATUSES } from "./ServiceValidationSkeletons"
import { saveNavContext, filtersToQuery, isDetailAvailable } from "./navContext"
import { downloadBlob, timestampedName } from "./downloadFile"
import {
  dateRangeValue, DATE_RANGE_DEFAULT,
  resolveFiltersFromUrl, filtersToSearchParams,
} from "./filterDefaults"
import DateRangeFilter from "./DateRangeFilter"
import { routeHealthMeta, routeHealthLevelOptions } from "./routeHealthMeta"

// Tablero Kanban de servicios TP agrupados por estatus del Trip.
// SIN drag & drop: vista de lectura con navegación directa al detalle.
// Cada columna carga de PAGE_SIZE en PAGE_SIZE con "Cargar más".
const PAGE_SIZE = 20

const PLACES = listPlaces()
const placeLabel = (id) => {
  if (id == null || id === "") return null
  const p = PLACES.find((x) => String(x.value) === String(id))
  return p?.label || id
}
const typeLabel = (t, v) => (v ? (t(v) !== v ? t(v) : v) : null)

const makeDefaultFilters = () => {
  const { date_from, date_to } = dateRangeValue(DATE_RANGE_DEFAULT)
  return {
    company_id: "", code: "", plate: "", place_id: "",
    validated: "false", route_health: "",
    date_range: DATE_RANGE_DEFAULT, date_from, date_to,
  }
}

// Menú de acciones a nivel de TARJETA (Tomar/Liberar). Mismo patrón
// ButtonDropdown (reactstrap) que el menú de opciones, sin librerías nuevas.
// Estado `isOpen` local. `heldByMe` con la MISMA fórmula del Detalle: on-hold
// propio -> "Liberar"; si no -> "Tomar". CRÍTICO: la tarjeta está envuelta en un
// <Link> con onClick que toma el servicio; por eso el toggle y cada item hacen
// stopPropagation Y preventDefault, para que abrir el menú o pulsar una acción
// NO navegue por el Link ni dispare la toma al hacer click en la tarjeta.
const ServiceActionsMenu = ({ t, service, currentSub, onTake, onRelease, onViewActions, canWrite, canReleaseOnHold, canSeeActions, pulse, onFirstOpen }) => {
  const [isOpen, setIsOpen] = useState(false)
  // TODO(my-services): el payload de tarjeta (ServiceListItem en pt-trip) puede
  // NO incluir `onhold_by`. Con la fórmula del Detalle, ausente -> heldByMe
  // false -> se muestra "Tomar" (fail-safe), nunca "Liberar", y no se rompe.
  const heldByMe = !!service.onhold_by && !!currentSub && service.onhold_by === currentSub
  // FEAT-002: con el permiso services:validation:on_hold::write se puede liberar
  // un hold AJENO (lo tomó otro usuario) para dejar el servicio tomable.
  const canReleaseForeign = !!service.onhold_by && !heldByMe && !!canReleaseOnHold
  const stop = (e) => { e.stopPropagation(); e.preventDefault() }
  // Al abrir por primera vez apagamos el pulse de descubribilidad en TODAS las
  // tarjetas (estado a nivel de vista + localStorage), no solo en esta.
  const toggle = () => {
    setIsOpen((v) => {
      if (!v && onFirstOpen) onFirstOpen()
      return !v
    })
  }
  return (
    <ButtonDropdown isOpen={isOpen} toggle={toggle} onClick={stop}>
      <DropdownToggle color="light" size="sm"
        className={`sv-card-actions-toggle${pulse ? " sv-cta-pulse" : ""}`}
        title={t("Actions") || "Acciones"} aria-label={t("Actions") || "Acciones"}
        onClick={stop}>
        <i className="bx bx-dots-vertical-rounded" />
      </DropdownToggle>
      <DropdownMenu end className="custom-dropdown-menu">
        {/* Tomar/Liberar son ESCRITURAS. "Liberar" propio: canWrite. "Liberar
            ajeno" (hold de otro): requiere el permiso on_hold::write (FEAT-002). */}
        {heldByMe && canWrite && (
          <DropdownItem onClick={(e) => { stop(e); onRelease(service) }}>
            <i className="bx bx-lock-open-alt me-2" />
            {t("ReleaseHold") || "Liberar"}
          </DropdownItem>
        )}
        {canReleaseForeign && (
          <DropdownItem onClick={(e) => { stop(e); onRelease(service) }}>
            <i className="bx bx-lock-open-alt me-2" />
            {t("ReleaseHoldForeign") || "Liberar (tomado por otro)"}
          </DropdownItem>
        )}
        {!heldByMe && !service.onhold_by && canWrite && (
          <DropdownItem onClick={(e) => { stop(e); onTake(service) }}>
            <i className="bx bx-lock-alt me-2" />
            {t("TakeHold") || "Tomar"}
          </DropdownItem>
        )}
        {/* Ver bitácora técnica: LECTURA (permiso propuesto actions::read). */}
        {canSeeActions && (
          <DropdownItem onClick={(e) => { stop(e); onViewActions(service) }}>
            <i className="bx bx-history me-2" />
            {t("ViewActionLog") || "Ver bitácora técnica"}
          </DropdownItem>
        )}
      </DropdownMenu>
    </ButtonDropdown>
  )
}

const ServiceValidationKanban = ({ t }) => {
  const { getAccessTokenSilently, user } = useAuth0()
  // `sub` del usuario actual (Auth0): misma fuente que el Detalle/Lista para el
  // on-hold. Base del filtro "Mis servicios" y de la asignación al hacer click.
  const currentSub = user?.sub || null
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  // FUENTE ÚNICA DE VERDAD (PT-4075): los filtros APLICADOS derivan de la URL en
  // cada render, idéntico mecanismo que en la Lista. No hay estado `filters`
  // paralelo que pueda caer al default 'today' y pisar la URL de retorno. Como
  // ambas vistas derivan del MISMO URL, alternar list<->kanban conserva filtros.
  const applied = useMemo(
    () => resolveFiltersFromUrl(searchParams, makeDefaultFilters),
    [searchParams],
  )

  // Escribe los filtros aplicados en la URL (fuente única), preservando el
  // ``status`` de la URL (el kanban no lo filtra pero lo conserva para no
  // perderlo al volver a la Lista). Único punto de escritura: manejadores
  // explícitos (buscar, limpiar, cambiar rango de fecha).
  const writeUrl = useCallback(
    (nextFilters) => {
      const q = filtersToSearchParams(nextFilters)
      const prevStatus = searchParams.get("status")
      if (prevStatus && !q.status) q.status = prevStatus
      setSearchParams(q, { replace: true })
    },
    [setSearchParams, searchParams],
  )

  const [infoUser, setInfoUser] = useState({ permissions: [] })
  const [isLoading, setLoading] = useState(false)
  // Estado por columna: { [status]: { items, count, page, loadingMore } }.
  const [cols, setCols] = useState({})
  const [companies, setCompanies] = useState([])
  // `draft` = estado de edición de los inputs; se inicializa desde la URL y la
  // búsqueda con debounce lo vuelca a la URL (no a un `filters` local).
  const [draft, setDraft] = useState(() => resolveFiltersFromUrl(searchParams, makeDefaultFilters))
  // Export Excel (backend): estado de carga del botón.
  const [exportingXlsx, setExportingXlsx] = useState(false)
  // Menú de opciones (equivalente al de la Lista). El Kanban no tiene el
  // "Agrupar por empresa", así que se introduce un menú mínimo para alojar la
  // misma opción "Mis servicios" y mantener el control consistente entre vistas.
  const [optionsOpen, setOptionsOpen] = useState(false)
  // Filtro "Mis servicios": muestra solo los servicios tomados por el usuario
  // actual (on-hold). Filtra en cliente por `onhold_by === currentSub`.
  const [onlyMine, setOnlyMine] = useState(false)
  // Descubribilidad del NUEVO menú de acciones (Tomar/Liberar). Mismo patrón que
  // `scaleToolsNew` en el Detalle y que la Lista: pulse `sv-cta-pulse` (ya
  // definido en el SCSS, se detiene tras unas iteraciones y respeta
  // prefers-reduced-motion) sobre el toggle de acciones mientras el usuario no
  // lo haya abierto nunca. Estado a NIVEL DE VISTA (apaga el pulse en todas las
  // tarjetas al primer uso), leyendo la MISMA clave de localStorage que la Lista
  // para que ver el efecto en una vista lo apague también en la otra.
  const [actionsMenuNew, setActionsMenuNew] = useState(() => {
    try { return localStorage.getItem("sv_actions_menu_seen_v1") !== "1" }
    catch (_e) { return true }
  })
  const dismissActionsMenuNew = useCallback(() => {
    setActionsMenuNew(false)
    try { localStorage.setItem("sv_actions_menu_seen_v1", "1") } catch (_e) { /* noop */ }
  }, [])

  // Opciones del filtro de salud de ruta (mejor -> peor).
  const healthOptions = useMemo(() => routeHealthLevelOptions(t), [t])

  // ¿Hay algo que exportar? True si alguna columna tiene al menos una tarjeta.
  const hasItems = useMemo(
    () => Object.values(cols).some((c) => (c?.items?.length || 0) > 0),
    [cols],
  )

  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({ data: Login.data, loading: Login.loading }),
  )
  const { data, loading } = useSelector(LoginProperties)

  useEffect(() => {
    if (data?.data) setInfoUser(data.data)
  }, [data, loading])

  const canList = infoUser.permissions?.includes(Permissions.SERVICE_VALIDATION_LIST)
  // Tomar un servicio (on-hold) es una ESCRITURA: igual que el Detalle, exige
  // SERVICE_VALIDATION_WRITE. Sin este permiso el click NO debe disparar el
  // hold (evita POST no autorizados de usuarios de solo-lectura).
  const canWrite = infoUser.permissions?.includes(Permissions.SERVICE_VALIDATION_WRITE)
  // FEAT-002: permiso para liberar un on-hold AJENO (lo tomó otro usuario) y
  // dejar el servicio tomable por un tercero. El backend es la autoridad final.
  const canReleaseOnHold = infoUser.permissions?.includes(Permissions.SERVICE_VALIDATION_ON_HOLD_WRITE)
  // Visibilidad de la bitácora técnica (gateada con SERVICE_VALIDATION_READ).
  const canSeeActions = infoUser.permissions?.includes(Permissions.SERVICE_VALIDATION_READ)

  const authHeader = useCallback(async () => {
    const accessToken = await getAccessTokenSilently()
    return { Authorization: `Bearer ${accessToken}` }
  }, [getAccessTokenSilently])

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const token = await authHeader()
        const resp = await getOrgsAdmin(token)
        const opts = (resp || []).map((o) => ({ label: o.name, value: o.partner_id }))
        if (active) setCompanies(opts)
      } catch (_e) {
        if (active) setCompanies([])
      }
    })()
    return () => { active = false }
  }, [authHeader])

  // Filtros efectivos (sin date_range, que es solo UI).
  const effectiveFilters = useCallback(() => {
    const opts = {}
    Object.entries(applied).forEach(([k, v]) => {
      if (k === "date_range") return
      if (v !== "" && v != null) opts[k] = v
    })
    return opts
  }, [applied])

  const load = useCallback(async () => {
    if (!canList) return
    setLoading(true)
    try {
      const token = await authHeader()
      // Primera página de cada columna: kanban con size_per_status=PAGE_SIZE.
      const board = await getServiceValidationKanban(token, { ...effectiveFilters(), size_per_status: PAGE_SIZE })
      const boardCols = board?.columns || []
      const byStatus = new Map(boardCols.map((c) => [c.status, c]))
      const next = {}
      TRIP_STATUSES.forEach((status) => {
        const c = byStatus.get(status)
        next[status] = {
          items: c?.items || [],
          count: c?.count ?? (c?.items?.length || 0),
          page: 1,
          loadingMore: false,
        }
      })
      setCols(next)
      const items = Object.entries(next).flatMap(([status, c]) =>
        (c.items || []).filter((s) => isDetailAvailable({ ...s, status })).map((s) => ({ service_code: s.service_code, validated: !!s.validated })),
      )
      saveNavContext({
        view: "kanban",
        returnUrl: `/service-validation/kanban${filtersToQuery(applied)}`,
        items,
      })
    } catch (_e) {
      setCols({})
    } finally {
      setLoading(false)
    }
  }, [authHeader, canList, effectiveFilters, applied])

  useEffect(() => { load() }, [load])

  // Resincroniza el `draft` de los inputs cuando la URL cambia por fuera (volver
  // del detalle, alternar list<->kanban). Los filtros aplicados derivan de la
  // URL; el draft debe reflejarlos para que los inputs no queden desfasados.
  useEffect(() => {
    setDraft(applied)
  }, [applied])

  // Cargar más de una columna: siguiente página vía list_services(status).
  const loadMore = useCallback(async (status) => {
    const col = cols[status]
    if (!col || col.loadingMore) return
    setCols((prev) => ({ ...prev, [status]: { ...prev[status], loadingMore: true } }))
    try {
      const token = await authHeader()
      const nextPage = col.page + 1
      const resp = await getServiceValidationList(token, {
        ...effectiveFilters(), status, page: nextPage, size: PAGE_SIZE,
      })
      const newItems = Array.isArray(resp) ? resp : resp?.items || []
      setCols((prev) => {
        const c = prev[status]
        // Evita duplicados por trip_uuid/service_code.
        const seen = new Set((c.items || []).map((s) => s.trip_uuid || s.service_code))
        const merged = [...c.items, ...newItems.filter((s) => !seen.has(s.trip_uuid || s.service_code))]
        return { ...prev, [status]: { ...c, items: merged, page: nextPage, loadingMore: false } }
      })
    } catch (_e) {
      setCols((prev) => ({ ...prev, [status]: { ...prev[status], loadingMore: false } }))
    }
  }, [authHeader, cols, effectiveFilters])

  const onChangeDraft = (field) => (e) => setDraft((d) => ({ ...d, [field]: e.target.value }))
  const onChangeDateRange = ({ range, date_from, date_to }) =>
    setDraft((d) => ({ ...d, date_range: range, date_from, date_to }))

  // Búsqueda automática con debounce (mismo criterio que la vista de lista):
  // aplicar = escribir la URL (fuente única), de ahí derivan `applied` y la
  // carga. Solo escribe si el draft difiere de lo aplicado, para no pisar la URL
  // en el montaje/resincronización (lo que reintroduciría el default 'today').
  useEffect(() => {
    if (JSON.stringify(draft) === JSON.stringify(applied)) return
    const handle = setTimeout(() => writeUrl(draft), 400)
    return () => clearTimeout(handle)
  }, [draft, applied, writeUrl])

  const clearFilters = () => { const d = makeDefaultFilters(); setDraft(d); writeUrl(d) }

  // Exportar a Excel: lo genera el BACKEND con los mismos filtros aplicados
  // (incluida la salud de ruta), recorriendo todas las páginas. Aquí solo se
  // arman los params y se dispara la descarga del Blob.
  const onExportXlsx = useCallback(async () => {
    if (!canList) return
    setExportingXlsx(true)
    try {
      const token = await authHeader()
      const blob = await getServiceValidationExport(token, effectiveFilters())
      downloadBlob(blob, timestampedName("servicios-validacion"))
    } catch (_e) {
      // Silencioso: el botón vuelve a estar disponible para reintentar.
    } finally {
      setExportingXlsx(false)
    }
  }, [authHeader, canList, effectiveFilters])

  // Toma el servicio (on-hold) para el usuario actual al hacer click en una
  // tarjeta, ADEMÁS de navegar al detalle (el <Link> sigue navegando). Reutiliza
  // postServiceValidationHold (misma mutación que Detalle/Lista). No roba un hold
  // ajeno. Tras tomarlo refresca el tablero para que se refleje (y lo incluya el
  // filtro "Mis servicios"). Errores silenciosos: no bloquean la navegación.
  const takeService = useCallback(async (s) => {
    // Gate de ESCRITURA: sin SERVICE_VALIDATION_WRITE el click solo navega al
    // detalle, nunca dispara el hold (consistente con el Detalle).
    if (!canWrite || !s?.service_code) return
    if (s.onhold_by === currentSub) return
    if (s.onhold_by && s.onhold_by !== currentSub) return
    try {
      await postServiceValidationHold(await authHeader(), s.service_code)
      load()
    } catch (e) {
      // El hold falló (403 sin permiso, 409 hold ajeno, red). La navegación al
      // detalle NO se bloquea, pero avisamos para que la toma no quede rota de
      // forma invisible (toast discreto, no bloqueante). Solo en el error.
      toast.error(
        e?.response?.data?.detail || t("TakeServiceError") || "No se pudo tomar el servicio",
        { autoClose: 3000 },
      )
    }
  }, [authHeader, canWrite, currentSub, load, t])

  // Libera el on-hold de la tarjeta (mutación "liberar" del Detalle, reutiliza
  // postServiceValidationRelease). Solo libera si el hold es propio. Tras
  // liberar recarga el tablero. El error se avisa con un toast discreto (solo en
  // el camino de error).
  const releaseService = useCallback(async (s) => {
    // Liberar un hold ajeno lo gobierna SOLO on_hold::write (FEAT-002): no
    // exigir canWrite cuando hay canReleaseOnHold, de lo contrario el item
    // "Liberar (tomado por otro)" se muestra pero el click es un no-op. La
    // autoridad final es el backend (gating por permiso en el endpoint).
    if ((!canWrite && !canReleaseOnHold) || !s?.service_code) return
    // Libero lo que es mío O, con el permiso on_hold::write, un hold ajeno
    // (FEAT-002) para que otro usuario pueda tomarlo.
    if (s.onhold_by !== currentSub && !canReleaseOnHold) return
    try {
      await postServiceValidationRelease(await authHeader(), s.service_code)
      load()
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || t("ReleaseServiceError") || "No se pudo liberar el servicio",
        { autoClose: 3000 },
      )
    }
  }, [authHeader, canWrite, canReleaseOnHold, currentSub, load, t])

  const placeOptions = useMemo(() => PLACES.map((p) => ({ label: p.label, value: p.value })), [])
  const validationOptions = useMemo(
    () => [
      { label: t("ToValidate"), value: "false" },
      { label: t("Validated"), value: "true" },
    ],
    [t],
  )
  const healthSelectOptions = useMemo(
    () => [{ label: t("All") || "Todas", value: "" }, ...healthOptions],
    [t, healthOptions],
  )

  const displayColumns = useMemo(() => (
    TRIP_STATUSES.map((status) => {
      const c = cols[status]
      const rawItems = c?.items || []
      // Filtro "Mis servicios": conserva solo las tarjetas tomadas por el
      // usuario actual. Se recalcula el `count` mostrado en consecuencia.
      // TODO(my-services): el payload de fila de kanban (ServiceListItem) aún NO
      // expone `onhold_by`. El backend debe añadir `onhold_by` a
      // _service_item_from_row / ServiceListItem (pt-trip) para que este filtro
      // devuelva tarjetas. Hasta entonces, con el filtro activo las columnas
      // quedan vacías.
      const items = onlyMine
        ? rawItems.filter((s) => s.onhold_by && s.onhold_by === currentSub)
        : rawItems
      return {
        status,
        status_label: t(`TripStatus_${status}`) || status,
        count: onlyMine ? items.length : (c?.count ?? 0),
        items,
        loadingMore: c?.loadingMore || false,
        hasMore: onlyMine ? false : (c ? (c.items.length < c.count) : false),
      }
    })
  ), [cols, t, onlyMine, currentSub])

  // Encabezado reutilizable (título + toggle + recargar + export).
  const headerRow = (
    <Row className="pb-2">
      <Col xs={12} md={6} className="d-flex align-items-center">
        <CardTitle className="h4 mb-0">{t("Requests")}</CardTitle>
      </Col>
      <Col xs={12} md={6} className="mt-2 mt-md-0 d-flex justify-content-md-end align-items-center flex-wrap gap-2">
        <ServiceValidationViewToggle active="kanban" />
        {/* Menú de opciones del Kanban: aloja el filtro "Mis servicios" (el
            Kanban no tiene "Agrupar por empresa", así que se añade un menú
            mínimo equivalente al de la Lista para mantener el control). */}
        <ButtonDropdown isOpen={optionsOpen} toggle={() => setOptionsOpen((v) => !v)}>
          <DropdownToggle className="sv-options-dropdown dropdown-toggle btn btn-secondary" caret
            title={t("Options") || "Opciones"} aria-label={t("Options") || "Opciones"}>
            <i className="bx bx-dots-vertical-rounded" />
          </DropdownToggle>
          <DropdownMenu end className="custom-dropdown-menu">
            <DropdownItem onClick={() => setOnlyMine((v) => !v)} data-tour="sv-my-services">
              <i className={`bx ${onlyMine ? "bx-check" : "bx-user"} me-2`} />
              {t("MyServices") || "Mis servicios"}
            </DropdownItem>
          </DropdownMenu>
        </ButtonDropdown>
        <button
          type="button"
          className={`btn-import-coke ${isLoading ? "refreshing" : ""}`}
          onClick={load}
          disabled={isLoading}
          title={t("Reload") || "Recargar"}
          aria-label={t("Reload") || "Recargar"}
        >
          <i className="bx bx-revision import-coke-icon" />
        </button>
        {canList && (
          <Button size="sm" color="success" outline onClick={onExportXlsx}
            disabled={exportingXlsx || isLoading || !hasItems}
            title={t("ExportExcel") || "Exportar Excel"}>
            <i className={`bx ${exportingXlsx ? "bx-loader bx-spin" : "bx-file"} me-1`} />
            {t("ExportExcel") || "Exportar Excel"}
          </Button>
        )}
      </Col>
    </Row>
  )

  return (
    <div className="page-content">
      {/* Contenedor de toasts local a la vista: solo para el aviso de error al
          tomar un servicio (camino de error del hold). */}
      <ToastContainer position="bottom-right" newestOnTop closeOnClick pauseOnHover />
      <Container fluid>
        <BreadcrumbLink title={t("Services")} link="/services/request" breadcrumbItem={t("ServiceValidation")} />

        {!canList ? (
          <Card className="mb-3">
            <CardBody>
              {headerRow}
              <div className="orange-separator" />
              <Alert color="warning" className="mb-0">{t("NoPermissionsViewServices")}</Alert>
            </CardBody>
          </Card>
        ) : (
          <>
            <Card className="mb-3">
              <CardBody>
                {headerRow}
                <div className="orange-separator" />
                <Row className="sv-filters g-2 align-items-end">
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Label className="form-label mb-1">{t("Company")}</Label>
                    <Select
                      classNamePrefix="select2-selection"
                      className="sv-filter-select"
                      placeholder={t("Partner") || t("Company")}
                      options={companies}
                      isClearable
                      value={companies.find((c) => String(c.value) === String(draft.company_id)) || null}
                      onChange={(opt) => setDraft((d) => ({ ...d, company_id: opt?.value ?? "" }))}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Label className="form-label mb-1">{t("Code")}</Label>
                    <Input className="sv-filter-control" value={draft.code} placeholder="TP-123"
                      onChange={onChangeDraft("code")}
                      onKeyDown={(e) => { if (e.key === "Enter") writeUrl(draft) }} />
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Label className="form-label mb-1">{t("Plate")}</Label>
                    <Input className="sv-filter-control" value={draft.plate}
                      onChange={onChangeDraft("plate")}
                      onKeyDown={(e) => { if (e.key === "Enter") writeUrl(draft) }} />
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Label className="form-label mb-1">{t("Place") || "Plaza"}</Label>
                    <Select
                      classNamePrefix="select2-selection"
                      className="sv-filter-select"
                      placeholder={t("All") || "Todas"}
                      options={placeOptions}
                      isClearable
                      value={placeOptions.find((o) => String(o.value) === String(draft.place_id)) || null}
                      onChange={(opt) => setDraft((d) => ({ ...d, place_id: opt?.value ?? "" }))}
                    />
                  </Col>
                </Row>
                <Row className="sv-filters g-2 align-items-end mt-1">
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Label className="form-label mb-1">{t("Validation")} *</Label>
                    <Select
                      classNamePrefix="select2-selection"
                      className="sv-filter-select"
                      placeholder={t("Validation")}
                      options={validationOptions}
                      value={validationOptions.find((o) => o.value === draft.validated) || null}
                      onChange={(opt) => setDraft((d) => ({ ...d, validated: opt?.value ?? "" }))}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Label className="form-label mb-1">{t("RouteHealth") || "Salud de ruta"}</Label>
                    <Select
                      classNamePrefix="select2-selection"
                      className="sv-filter-select"
                      placeholder={t("All") || "Todas"}
                      options={healthSelectOptions}
                      value={healthSelectOptions.find((o) => o.value === draft.route_health) || null}
                      onChange={(opt) => setDraft((d) => ({ ...d, route_health: opt?.value ?? "" }))}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Label className="form-label mb-1">{t("Date")} *</Label>
                    <DateRangeFilter
                      t={t}
                      value={{ range: draft.date_range, date_from: draft.date_from, date_to: draft.date_to }}
                      onChange={onChangeDateRange}
                    />
                  </Col>
                </Row>
                <div className="orange-separator" />
                <div className="d-flex flex-wrap gap-2">
                  <Button size="sm" color="light" onClick={clearFilters}>{t("Clear")}</Button>
                </div>
              </CardBody>
            </Card>

            {isLoading ? (
              <KanbanSkeleton />
            ) : (
              <div className="d-flex gap-3 pb-3" style={{ overflowX: "auto" }}>
                {displayColumns.map((col) => (
                  <div key={col.status} style={{ minWidth: 280, flex: "0 0 280px" }}>
                    <Card className="h-100">
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <strong>{col.status_label}</strong>
                          <Badge color={col.count > 0 ? "info" : "light"} pill>
                            {col.count}
                          </Badge>
                        </div>
                        <div className="d-flex flex-column gap-2">
                          {col.items.length === 0 ? (
                            <span className="text-muted small">{t("NoResults")}</span>
                          ) : (
                            col.items.map((s) => {
                              // Solo los finalizados tienen detalle: el resto es
                              // una tarjeta sin enlace.
                              const open = isDetailAvailable({ ...s, status: col.status })
                              const Wrap = open ? Link : "div"
                              const wrapProps = open
                                ? { to: `/service-validation/${s.service_code}`, onClick: () => takeService(s) }
                                : {}
                              return (
                              <Wrap
                                key={s.trip_uuid || s.service_code}
                                className="text-decoration-none"
                                {...wrapProps}
                              >
                                <Card className="mb-0 border">
                                  <CardBody className="p-2">
                                    <div className="d-flex justify-content-between align-items-center gap-1">
                                      <span className="fw-medium">{s.service_code || "-"}</span>
                                      <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                        {/* Salud de ruta (GRS), si está disponible. */}
                                        {s.route_health_level && (() => {
                                          const h = routeHealthMeta(s.route_health_level, s.route_health_score, t)
                                          return (
                                            <Badge color={h.color}
                                              title={h.score != null ? `${t("RouteHealth")}: ${h.label} (${h.score})` : `${t("RouteHealth")}: ${h.label}`}>
                                              <i className="bx bx-pulse" />{h.score != null ? ` ${h.score}` : ""}
                                            </Badge>
                                          )
                                        })()}
                                        {/* El estatus de validación solo aplica a
                                            servicios finalizados. */}
                                        {col.status === "FINISHED" && (
                                          <Badge color={s.validated ? "success" : "warning"}>
                                            {s.validated ? t("Validated") : t("ToValidate")}
                                          </Badge>
                                        )}
                                        {/* Menú de acciones (Tomar/Liberar) a nivel
                                            de tarjeta: solo con permiso de ESCRITURA. */}
                                        {(canWrite || canReleaseOnHold || canSeeActions) && (
                                          <ServiceActionsMenu
                                            t={t}
                                            service={s}
                                            currentSub={currentSub}
                                            onTake={takeService}
                                            onRelease={releaseService}
                                            onViewActions={(sv) => navigate(`/service-validation/${sv.service_code}/actions`)}
                                            canWrite={canWrite}
                                            canReleaseOnHold={canReleaseOnHold}
                                            canSeeActions={canSeeActions}
                                            pulse={actionsMenuNew}
                                            onFirstOpen={dismissActionsMenuNew}
                                          />
                                        )}
                                      </div>
                                    </div>
                                    <div className="d-flex justify-content-between small mt-1">
                                      <span className="text-truncate" style={{ maxWidth: 150 }}
                                        title={s.partner_name || s.company_id || ""}>
                                        {s.partner_name || s.company_id || "-"}
                                      </span>
                                      <span className="text-muted flex-shrink-0">
                                        {s.kms != null ? `${Number(s.kms).toFixed(1)} km` : "-"}
                                        {s.duration != null ? ` · ${Math.round(s.duration / 60)} min` : ""}
                                      </span>
                                    </div>
                                    <div className="d-flex justify-content-between small text-muted mt-1">
                                      <span>{s.plate || "-"}</span>
                                      <span>
                                        {s.scheduled_start
                                          ? `${fmtMxDate(s.scheduled_start)} ${fmtMxTime(s.scheduled_start)}`
                                          : (s.created_at ? String(s.created_at).slice(0, 10) : "")}
                                      </span>
                                    </div>
                                    <div className="small text-muted mt-1">
                                      <i className="bx bx-map me-1" />
                                      {placeLabel(s.place) || "-"}
                                      {typeLabel(t, s.service_type) ? ` · ${typeLabel(t, s.service_type)}` : ""}
                                    </div>
                                  </CardBody>
                                </Card>
                              </Wrap>
                              )
                            })
                          )}
                          {/* Cargar más (20 en 20) al final de la columna. */}
                          {col.hasMore && (
                            <Button size="sm" color="light" className="w-100"
                              onClick={() => loadMore(col.status)} disabled={col.loadingMore}>
                              {col.loadingMore
                                ? <Spinner size="sm" />
                                : <>{t("LoadMore") || "Cargar más"} ({col.items.length}/{col.count})</>}
                            </Button>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Container>
    </div>
  )
}

export default withTranslation()(ServiceValidationKanban)
